import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { uploadExerciseEvidence, saveExerciseEvidence, completeExercise } from '../../../services/exerciseService';
import { useAuth } from '../../../contexts/AuthContext';

const ExerciseCard = ({ 
  exercise, 
  onComplete, 
  onUpdateReflection, 
  isPremium = false,
  className = '' 
}) => {
  const { user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [reflection, setReflection] = useState(exercise?.reflection || '');
  const [showReflectionInput, setShowReflectionInput] = useState(false);
  const [showEvidenceUpload, setShowEvidenceUpload] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadError(null);

    try {
      const uploadPromises = files.map(async (file) => {
        const result = await uploadExerciseEvidence(file, exercise.id, user.id);
        if (result.success) {
          return {
            url: result.url,
            path: result.path,
            fileName: result.fileName,
            fileSize: result.fileSize,
            mimeType: result.mimeType,
            originalName: file.name,
            type: 'file_upload'
          };
        } else {
          throw new Error(result.error);
        }
      });

      const results = await Promise.all(uploadPromises);
      setUploadedFiles(prev => [...prev, ...results]);
      
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleComplete = async () => {
    if (!user) {
      setUploadError('User not authenticated');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Prepare evidence data
      const evidenceData = uploadedFiles.map(file => ({
        userId: user.id,
        type: file.type,
        fileName: file.originalName,
        fileSize: file.fileSize,
        url: file.url,
        mimeType: file.mimeType,
        description: `Evidence for ${exercise.title}`
      }));

      // Complete the exercise with evidence
      const result = await completeExercise(exercise.id, {
        userId: user.id,
        evidence: evidenceData,
        reflection: reflection?.trim() || null,
        progress: exercise.steps?.map((step, index) => ({
          userId: user.id,
          stepNumber: index + 1,
          stepTitle: step,
          isCompleted: true,
          completedAt: new Date().toISOString()
        }))
      });

      if (result.success) {
        const completedExercise = {
          ...exercise,
          completed: true,
          completedAt: new Date().toISOString(),
          reflection: reflection?.trim() || null,
          evidence: uploadedFiles.length > 0 ? 'Evidence files have been uploaded and saved.' : exercise.evidence
        };
        onComplete(completedExercise);
        setShowReflectionInput(false);
        setShowEvidenceUpload(false);
        setUploadedFiles([]);
      } else {
        setUploadError(result.error);
      }
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleReflectionSave = () => {
    onUpdateReflection(exercise?.id, reflection?.trim());
    setShowReflectionInput(false);
  };

  const getDueDateStatus = () => {
    if (exercise?.completed) return 'completed';
    
    const dueDate = new Date(exercise.dueDate);
    const today = new Date();
    const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'overdue';
    if (diffDays === 0) return 'due-today';
    if (diffDays <= 2) return 'due-soon';
    return 'upcoming';
  };

  const getStatusConfig = () => {
    const status = getDueDateStatus();
    const configs = {
      completed: {
        icon: 'CheckCircle',
        color: 'text-success',
        bgColor: 'bg-success/10',
        borderColor: 'border-success/20',
        label: 'Completed'
      },
      overdue: {
        icon: 'AlertCircle',
        color: 'text-error',
        bgColor: 'bg-error/10',
        borderColor: 'border-error/20',
        label: 'Overdue'
      },
      'due-today': {
        icon: 'Clock',
        color: 'text-warning',
        bgColor: 'bg-warning/10',
        borderColor: 'border-warning/20',
        label: 'Due Today'
      },
      'due-soon': {
        icon: 'Clock',
        color: 'text-warning',
        bgColor: 'bg-warning/10',
        borderColor: 'border-warning/20',
        label: 'Due Soon'
      },
      upcoming: {
        icon: 'Calendar',
        color: 'text-muted-foreground',
        bgColor: 'bg-muted/50',
        borderColor: 'border-border',
        label: 'Upcoming'
      }
    };
    return configs?.[status];
  };

  const statusConfig = getStatusConfig();
  const formattedDueDate = new Date(exercise.dueDate)?.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });

  return (
    <div className={`bg-card border ${statusConfig?.borderColor} rounded-lg shadow-sm hover:shadow-md transition-all duration-200 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-lg font-semibold text-foreground truncate">
                {exercise?.title}
              </h3>
              {exercise?.isPremium && !isPremium && (
                <Icon name="Crown" size={16} className="text-warning flex-shrink-0" />
              )}
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center space-x-1">
                <Icon name="Clock" size={14} />
                <span>{exercise?.estimatedMinutes} min</span>
              </div>
              <div className="flex items-center space-x-1">
                <Icon name="Calendar" size={14} />
                <span>{formattedDueDate}</span>
              </div>
              <div className={`flex items-center space-x-1 px-2 py-1 rounded-full ${statusConfig?.bgColor} ${statusConfig?.color}`}>
                <Icon name={statusConfig?.icon} size={12} />
                <span className="text-xs font-medium">{statusConfig?.label}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 ml-4">
            {exercise?.completed ? (
              <div className="flex items-center space-x-2 text-success">
                <Icon name="CheckCircle" size={20} />
                <span className="text-sm font-medium">Done</span>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
                iconPosition="right"
              >
                {isExpanded ? 'Collapse' : 'View Details'}
              </Button>
            )}
          </div>
        </div>
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* Description */}
          {exercise?.description && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Description</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {exercise?.description}
              </p>
            </div>
          )}

          {/* Steps */}
          <div>
            <h4 className="text-sm font-medium text-foreground mb-3">Steps to Complete</h4>
            <div className="space-y-2">
              {exercise?.steps?.map((step, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium">{index + 1}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Evidence/Verification */}
          {exercise?.evidence && (
            <div className="bg-muted/50 rounded-lg p-3">
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center space-x-2">
                <Icon name="CheckSquare" size={16} />
                <span>Evidence of Completion</span>
              </h4>
              <p className="text-sm text-muted-foreground">
                {typeof exercise?.evidence === 'string' 
                  ? exercise.evidence 
                  : 'Evidence files have been uploaded and saved.'}
              </p>
            </div>
          )}

          {/* Evidence Upload Section */}
          {!exercise?.completed && typeof exercise?.evidence === 'string' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 dark:bg-blue-950/20 dark:border-blue-800/30">
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center space-x-2">
                <Icon name="Upload" size={16} />
                <span>Submit Evidence</span>
              </h4>
              
              {/* Upload Error */}
              {uploadError && (
                <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 dark:bg-red-950/20 dark:border-red-800/30 dark:text-red-400">
                  {uploadError}
                </div>
              )}

              {/* File Upload */}
              <div className="space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  iconName="Upload"
                  iconPosition="left"
                >
                  {isUploading ? 'Uploading...' : 'Choose Files'}
                </Button>

                <p className="text-xs text-muted-foreground">
                  Supported formats: Images (JPG, PNG, GIF, WebP), PDF, Word docs, text files. Max 10MB per file.
                </p>
              </div>

              {/* Uploaded Files */}
              {uploadedFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  <h5 className="text-xs font-medium text-foreground">Uploaded Files:</h5>
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border">
                      <div className="flex items-center space-x-2">
                        <Icon name="File" size={14} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">{file.originalName}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(file.fileSize / 1024 / 1024).toFixed(1)}MB)
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeUploadedFile(index)}
                        iconName="X"
                        className="h-6 w-6 p-0"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Reflection Section */}
          {exercise?.completed && exercise?.reflection && (
            <div className="bg-success/5 border border-success/20 rounded-lg p-3">
              <h4 className="text-sm font-medium text-success mb-2 flex items-center space-x-2">
                <Icon name="MessageSquare" size={16} />
                <span>Your Reflection</span>
              </h4>
              <p className="text-sm text-muted-foreground italic">
                "{exercise?.reflection}"
              </p>
              <div className="text-xs text-muted-foreground mt-2">
                Completed on {new Date(exercise.completedAt)?.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          )}

          {/* Reflection Input */}
          {showReflectionInput && !exercise?.completed && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-foreground">Add Reflection (Optional)</h4>
              <textarea
                value={reflection}
                onChange={(e) => setReflection(e?.target?.value)}
                placeholder="What did you learn from this exercise? How will it help your career development?"
                className="w-full p-3 border border-border rounded-lg text-sm resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={3}
              />
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReflectionSave}
                >
                  Save Reflection
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowReflectionInput(false);
                    setReflection(exercise?.reflection || '');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!exercise?.completed && (
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowReflectionInput(!showReflectionInput)}
                iconName="MessageSquare"
                iconPosition="left"
              >
                Add Reflection
              </Button>
              
              <Button
                variant="default"
                size="sm"
                onClick={handleComplete}
                disabled={isUploading}
                iconName={isUploading ? "Loader2" : "Check"}
                iconPosition="left"
              >
                {isUploading ? 'Completing...' : 'Mark Complete'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExerciseCard;